import axios from 'axios';
import { Dispatch } from 'redux';

// Action types
export const FETCH_YEARBOOK_POSES_REQUEST = 'FETCH_YEARBOOK_POSES_REQUEST';
export const FETCH_YEARBOOK_POSES_SUCCESS = 'FETCH_YEARBOOK_POSES_SUCCESS';
export const FETCH_YEARBOOK_POSES_FAILURE = 'FETCH_YEARBOOK_POSES_FAILURE';
export const SKIP_YEARBOOK_SELECTION = 'SKIP_YEARBOOK_SELECTION';

// One pose as GET /api/v2/yearbook/:student_id lists it
// (Api::V2::YearbookController#list_eligible_images).
export interface YearbookPose {
  id: number;
  url: string;
  selected: boolean;
  filename: string;
}

// The DP2 gallery a yearbook request names (ENG-3574): its key (the gallery
// password) and its id. The server finds galleries by key and lets the id
// only choose among the ones that key matches, so the pair is sent whole or
// not at all. selectYearbookPickerDp2Gallery builds it.
export interface YearbookPickerGallery {
  id: number;
  key: string;
}

// Action creators
export const fetchYearbookPosesRequest = () => ({
  type: FETCH_YEARBOOK_POSES_REQUEST,
});

export const fetchYearbookPosesSuccess = (poses: YearbookPose[]) => ({
  type: FETCH_YEARBOOK_POSES_SUCCESS,
  payload: poses,
});

export const fetchYearbookPosesFailure = (error: string) => ({
  type: FETCH_YEARBOOK_POSES_FAILURE,
  payload: error,
});

// Thunk action to fetch yearbook poses. `gallery` names the DP2 gallery the
// parent opened, by key and id, so a student with galleries from more than
// one school year gets that gallery's poses (ENG-3574); without it the server
// picks the student's gallery and the request is the bare URL it has always
// been.
export const fetchYearbookPoses = (
  studentId: string,
  gallery?: YearbookPickerGallery | null,
) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      dispatch(fetchYearbookPosesRequest());

      const url = `/api/v2/yearbook/${studentId}`;
      const response = await (gallery
        ? axios.get<YearbookPose[]>(url, {
            params: {
              dp2_gallery_id: gallery.id,
              dp2_gallery_key: gallery.key,
            },
          })
        : axios.get<YearbookPose[]>(url));

      if (response.status >= 200 && response.status < 300) {
        dispatch(fetchYearbookPosesSuccess(response.data));
      } else {
        throw new Error('Failed to fetch yearbook poses');
      }
    } catch (error) {
      // axios rejects with an AxiosError, and the branch above throws an Error.
      dispatch(fetchYearbookPosesFailure((error as Error).message));
    }
  };
};

// Thunk to skip yearbook selection and go to order
export const skipYearbookSelectionAndGoToOrder = (skip: boolean) => ({
  type: SKIP_YEARBOOK_SELECTION,
  payload: {
    skipSelectionForSession: skip,
  },
});
